import { Link } from 'react-router-dom';
import { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="flex items-start gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer group"
      >
        <div className="w-20 h-14 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-800 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
            {article.title}
          </h4>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-eye-line"></i>
              </span>
              {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
            </span>
            <span>{article.publishDate}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="flex flex-col sm:flex-row gap-4 p-4 rounded-lg hover:bg-background-100 transition-colors cursor-pointer group"
      >
        <div className="w-full sm:w-48 h-32 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            <span className="inline-block px-2 py-0.5 text-xs font-medium bg-primary-50 text-primary-500 rounded-full mb-2 whitespace-nowrap">
              {article.categoryName}
            </span>
            <h3 className="text-base font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
              {article.title}
            </h3>
            <p className="text-sm text-foreground-600 line-clamp-2 mt-1.5 leading-relaxed">
              {article.summary}
            </p>
          </div>
          <div className="flex items-center gap-4 mt-2 text-xs text-foreground-500 flex-wrap">
            <span className="flex items-center gap-1">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-eye-line"></i>
              </span>
              {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万阅读` : `${article.views}阅读`}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-time-line"></i>
              </span>
              {article.publishDate}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-user-line"></i>
              </span>
              {article.author}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200/50 transition-all duration-300 cursor-pointer group h-full"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
      </div>
      <div className="p-4">
        <span className="inline-block px-2.5 py-0.5 text-xs font-medium bg-primary-50 text-primary-500 rounded-full mb-2 whitespace-nowrap">
          {article.categoryName}
        </span>
        <h3 className="text-base font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 mt-2 leading-relaxed">
          {article.summary}
        </p>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200/50">
          <div className="flex items-center gap-3 text-xs text-foreground-500 flex-wrap">
            <span className="flex items-center gap-1">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-eye-line"></i>
              </span>
              {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
            </span>
            <span>{article.publishDate}</span>
          </div>
          {article.hotScore >= 90 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-accent-50 text-accent-600 rounded-full whitespace-nowrap">
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-fire-line"></i>
              </span>
              热
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}